// src/components/home/HomeTab.jsx
// 홈 탭: 행동 중심 레이아웃 (예약 입력 → 메시지 발송)

import React from 'react';
import { useApp } from '../../context/AppContext';
import { MESSAGE_TEMPLATES } from '../../services/messageTemplates';

// ─── 유틸 ───

/**
 * Firestore Timestamp / ISO string / yyyy.MM.dd 등 다양한 형식을 Date로 변환
 * 파싱 실패 시 null 반환
 */
function toDate(value) {
  if (value === null || value === undefined || value === '') return null;

  // Firestore Timestamp: .toDate() 메서드가 있는 경우 (공식 SDK)
  if (typeof value?.toDate === 'function') {
    const d = value.toDate();
    return isNaN(d.getTime()) ? null : d;
  }

  // Firestore Timestamp 직렬화 형태: { seconds: N, nanoseconds: N }
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000);
  }

  // 이미 Date 객체
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  // 문자열: yyyy.MM.dd → yyyy-MM-dd 정규화 후 파싱
  const normalized = String(value).replace(/\./g, '-').trim();
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * 로컬 날짜 기준 상대시간 표시
 * - 1시간 이내: "방금 전"
 * - 오늘: "오늘"
 * - 어제: "1일 전"
 * - 2일 이상: "N일 전"
 */
function formatRelativeTime(value) {
  const date = toDate(value);
  if (!date) return '';

  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 60) return '방금 전';

  // 로컬 날짜 기준 비교
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((todayStart - dateStart) / 86400000);

  if (diffDays === 0) return '오늘';
  if (diffDays === 1) return '1일 전';
  return `${diffDays}일 전`;
}

function getGreeting() {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return { text: '좋은 아침이에요', emoji: '☀️' };
  if (h >= 12 && h < 18) return { text: '오후도 파이팅이에요', emoji: '💪' };
  if (h >= 18 && h < 22) return { text: '수고하셨어요', emoji: '🌙' };
  return { text: '야간 운영 중이시군요', emoji: '🌟' };
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

export default function HomeTab({ onTabChange, onSendToReservation }) {
  const { state, getRevisitCandidates, getTodayCheckins, getTodayCheckouts, markReviewRead, getCustomersWithStatus } = useApp();
  const { messages, reviews } = state;
  const unreadReviews = (reviews || []).filter(r => !r.read);

  const unreadPhones = new Set(unreadReviews.map(r => r.customerPhone).filter(Boolean));
  const unreadCustomerCount = getCustomersWithStatus().filter(c => unreadPhones.has(c.phone)).length;

  const greeting = getGreeting();
  const todayCheckins = getTodayCheckins();
  const todayCheckouts = getTodayCheckouts();
  const revisitCandidates = getRevisitCandidates();

  const todayStr = new Date().toISOString().split('T')[0];
  const todayMessages = messages.filter(m => {
    const d = toDate(m.sentAt);
    return d && d.toDateString() === new Date().toDateString();
  });

  const recentMessages = messages.slice(0, 4);
  const todayReservations = [...todayCheckins, ...todayCheckouts.filter(r => r.checkin !== todayStr)];

  return (
    <div className="tab-content">

      {/* ── 1. 인사 + 미니 요약 ── */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>
              {greeting.text} {greeting.emoji}
            </p>
            <p style={{ margin: '3px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
              오늘 체크인 {todayCheckins.length}건 · 메시지 {todayMessages.length}건 발송
            </p>
          </div>
        </div>
      </div>

      {/* ── 2. 미니 통계 ── */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 20,
      }}>
        {[
          { label: '오늘 예약', value: todayCheckins.length + todayCheckouts.length, color: 'var(--green)' },
          { label: '오늘 발송', value: todayMessages.length, color: 'var(--primary)' },
          { label: '재방문 대상', value: revisitCandidates.length, color: revisitCandidates.length > 0 ? 'var(--amber)' : 'var(--text-secondary)' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            background: 'white', borderRadius: 12,
            border: '1px solid var(--border)',
            padding: '10px 8px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* ── 새 리뷰 알림 ── */}
      {unreadCustomerCount > 0 && (
        <div
          className="card"
          style={{
            background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
            border: '1.5px solid #f59e0b',
            marginBottom: 12, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
          onClick={() => onTabChange('customers')}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 24 }}>⭐</span>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#78350f' }}>
                새 리뷰 {unreadCustomerCount}건이 도착했습니다!
              </div>
              <div style={{ fontSize: 12, color: '#92400e', marginTop: 1 }}>고객 탭에서 확인하세요 →</div>
            </div>
          </div>
        </div>
      )}

      {/* ── 3. 메인 CTA ── */}
      <button
        onClick={() => onTabChange('send')}
        style={{
          width: '100%',
          background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
          border: 'none',
          borderRadius: 18,
          padding: '22px 20px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 10,
          boxShadow: '0 4px 20px rgba(34,197,94,0.35)',
          textAlign: 'left',
        }}
      >
        <div style={{
          width: 52, height: 52, borderRadius: 14,
          background: 'rgba(255,255,255,0.22)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 26, flexShrink: 0,
        }}>
          📋
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: 'white', marginBottom: 4, lineHeight: 1.2 }}>
            예약만 붙여넣으면 자동으로 안내 메시지 발송
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', lineHeight: 1.4 }}>
            체크인 / 체크아웃 안내를 한 번에 보내세요
          </div>
        </div>
        <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.7)', flexShrink: 0 }}>›</div>
      </button>

      {/* ── 4. 보조 CTA ── */}
      <button
        onClick={() => onTabChange('marketing')}
        style={{
          width: '100%',
          background: 'white',
          border: '1.5px solid var(--border)',
          borderRadius: 14,
          padding: '14px 18px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          marginBottom: 24,
          textAlign: 'left',
        }}
      >
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: '#fffbeb',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 20, flexShrink: 0,
        }}>
          📣
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 2 }}>
            고객 재방문 유도하기
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            예전에 온 고객에게 다시 연락해보세요
            {revisitCandidates.length > 0 && (
              <span style={{
                marginLeft: 6, fontSize: 11,
                background: '#fef3c7', color: '#92400e',
                padding: '1px 6px', borderRadius: 10, fontWeight: 600,
              }}>
                {revisitCandidates.length}명 대상
              </span>
            )}
          </div>
        </div>
        <div style={{ fontSize: 18, color: 'var(--text-muted)', flexShrink: 0 }}>›</div>
      </button>

      {/* ── 5. 오늘 체크인 고객 ── */}
      {todayCheckins.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <p className="section-title" style={{ marginBottom: 10 }}>오늘 체크인 고객입니다</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {todayCheckins.map(res => (
              <TodayReservationRow
                key={res.id}
                res={res}
                type="checkin"
                onSend={() => onSendToReservation ? onSendToReservation(res) : onTabChange('send')}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── 6. 최근 발송 내역 ── */}
      <div style={{ marginBottom: 20 }}>
        <p className="section-title" style={{ marginBottom: 10 }}>최근 보낸 메시지</p>
        {recentMessages.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '28px 16px',
            background: 'white', borderRadius: 'var(--radius)',
            border: '1px solid var(--border)',
            color: 'var(--text-muted)', fontSize: 13,
          }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>💬</div>
            아직 발송 내역이 없습니다<br />
            <span style={{ fontSize: 12 }}>위 버튼을 눌러 첫 메시지를 보내보세요</span>
          </div>
        ) : (
          <div style={{
            background: 'white', borderRadius: 'var(--radius)',
            border: '1px solid var(--border)',
            overflow: 'hidden',
          }}>
            {recentMessages.map((msg, idx) => {
              const tmpl = MESSAGE_TEMPLATES[msg.templateId];
              const icon = tmpl?.icon || '💬';
              return (
                <div
                  key={msg.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 16px',
                    borderBottom: idx < recentMessages.length - 1 ? '1px solid var(--border-light)' : 'none',
                  }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: 'var(--green-light)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, flexShrink: 0,
                  }}>
                    {icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{msg.customerName}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0, marginLeft: 8 }}>
                        {formatRelativeTime(msg.sentAt)}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {msg.templateName}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
}

// ─── 오늘 예약 카드 ───
function TodayReservationRow({ res, type, onSend }) {
  const isCheckin = type === 'checkin';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      background: 'white', borderRadius: 12,
      border: `1.5px solid ${isCheckin ? 'var(--green-border, #bbf7d0)' : '#fde68a'}`,
      padding: '12px 14px',
    }}>
      {/* 타입 아이콘 */}
      <div style={{
        width: 38, height: 38, borderRadius: 10, flexShrink: 0,
        background: isCheckin ? 'var(--green-light)' : '#fef3c7',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
      }}>
        {isCheckin ? '🏠' : '👋'}
      </div>

      {/* 정보 */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <span style={{ fontWeight: 700, fontSize: 14 }}>{res.customerName}</span>
          <span style={{
            fontSize: 10, padding: '2px 6px', borderRadius: 8, fontWeight: 600,
            background: isCheckin ? 'var(--green-light)' : '#fef3c7',
            color: isCheckin ? 'var(--green-dark)' : '#92400e',
          }}>
            {isCheckin ? '체크인' : '체크아웃'}
          </span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {res.roomName} · {res.guests}명
        </div>
      </div>

      {/* 메시지 보내기 */}
      <button
        onClick={onSend}
        style={{
          fontSize: 12, fontWeight: 600, padding: '7px 12px',
          borderRadius: 10, flexShrink: 0,
          background: isCheckin ? 'var(--green)' : '#f59e0b',
          color: 'white', border: 'none', cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        메시지 보내기
      </button>
    </div>
  );
}
